import {
  createContext,
  useState,
  FunctionComponent,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import { useForceUpdate } from '@react-spring/shared'

import { setCache, removeCache, getCache } from '@/utils/cache'
import {
  setToken as setTokenStorage,
  removeToken,
  getToken,
  setRefreshToken as setRefreshTokenStorage,
  removeRefreshToken,
  getRefreshToken,
} from '@/utils/token'
import { ClinicAndFlags, IUser } from '../auth-types'
import { buildUserFromInterface, User } from '@/modules/Auth/classes/User'
import { buildClinicFromInterface, Clinic } from '@/modules/Auth/classes/Clinic'
import merge from 'deepmerge'
import httpClient from '@/services/httpClient'
import {
  AUTHENTICATED_2FA_TIME,
  REQUESTED_2FA_TIME,
} from '../pages/TwoFactorAuth/constants'
import * as Sentry from '@sentry/react'
import { IS_PRODUCTION } from '@/constants'

type TokenType = string
type updateUserCallback = (newUser: User) => User

export interface IAuthContext {
  token?: TokenType
  refreshToken?: TokenType
  user?: User
  users?: User[]
  clinic?: Clinic
  signIn(users: IUser[]): void
  choiceUser(userID: number, ip?: string): void
  setUserClinic(clinic: ClinicAndFlags): void
  signOut(deep?: boolean): void
  updateUser(call: updateUserCallback): void
  updateAllUsers(updatedUsers: User[]): void
}

const AuthContext = createContext<IAuthContext>(null)

export const AuthProvider: FunctionComponent = ({ children }) => {
  const [users, setAllUsers] = useState<User[]>(() => {
    const usersData: IUser[] = getCache<IUser[]>('users')
    if (usersData) {
      const mappedUsers = usersData.map(user => {
        const userId = user?.usuario?.id || null
        return buildUserFromInterface({
          ...user,
          usuario: {
            id: userId,
            ...user?.usuario,
          },
        })
      })

      return mappedUsers
    } else {
      return null
    }
  })

  const [user, setUser] = useState<User>(() => {
    const userId: number = getCache<number>('user')
    if (userId) {
      const users: IUser[] = getCache<IUser[]>('users')
      const user: IUser = users?.filter(u => u?.usuario?.id === userId)[0]
      return buildUserFromInterface(user)
    } else return null
  })

  const [clinic, setClinic] = useState<Clinic>(() => {
    const clinicAndFlags: ClinicAndFlags = getCache<ClinicAndFlags>('clinic')
    if (clinicAndFlags) {
      return buildClinicFromInterface(
        clinicAndFlags?.clinic,
        clinicAndFlags?.flags,
      )
    } else return null
  })

  const [token, setToken] = useState<TokenType>(() => getToken())
  const [refreshToken, setRefreshToken] = useState<TokenType>(() =>
    getRefreshToken(),
  )

  const forceUpdate = useForceUpdate()

  const signIn = useCallback((users: IUser[]): void => {
    setCache('users', users)
    const Users: User[] = users?.map(user => buildUserFromInterface(user))
    setAllUsers(Users)
  }, [])

  const choiceUser = useCallback(
    (userID: number, ip: string): void => {
      const users = getCache<IUser[]>('users')
      const user: IUser = users?.filter(u => u.usuario.id === userID)[0]
      user.ip = ip

      signIn(users)
      setTokenStorage(user?.token)
      setToken(user?.token)
      setRefreshTokenStorage(user?.refreshToken)
      setRefreshToken(user?.refreshToken)
      setCache('user', user?.usuario.id)
      const User: User = buildUserFromInterface(user)
      User.ip = ip
      setUser(User)

      if (IS_PRODUCTION) {
        Sentry.setTag('userId', user?.usuario?.id)
      }

      window?.Appcues?.identify(userID, {
        patientId: user?.usuario?.paciente?.id,
        patientName: user?.usuario?.nome,
        patientSocialName: user?.usuario?.paciente?.nomeSocial ?? '',
        clinicId: user?.usuario?.clinica?.id,
        clinicName: user?.usuario?.clinica?.nome,
      })
    },
    [signIn],
  )

  const updateUser = useCallback(
    (call: updateUserCallback): void => {
      const newUser = call(user)
      const users = getCache<IUser[]>('users')
      const cUser: IUser = users?.filter(u => u?.usuario?.id === user?.id)[0]
      const updatedUser: IUser = merge(cUser, {
        ...newUser.buildUserFromClass(),
      } as Partial<IUser>)
      const newAllUsers = []

      users?.forEach(u => {
        if (u?.usuario?.id === updatedUser?.usuario?.id) {
          newAllUsers?.push(updatedUser)
        } else {
          newAllUsers?.push(u)
        }
      })

      setCache('users', newAllUsers)
      setTokenStorage(newUser?.token)
      setToken(newUser?.token)
      setRefreshTokenStorage(newUser?.refreshToken)
      setRefreshToken(newUser?.refreshToken)
      setUser(newUser)
      forceUpdate()
    },
    [user, forceUpdate],
  )

  const updateAllUsers = useCallback(
    (updatedUsers: User[]) => {
      setAllUsers(updatedUsers)

      const newAllUsers = users.map(user => user.buildUserFromClass())

      setCache('users', newAllUsers)
    },
    [users],
  )

  const setUserClinic = useCallback((clinicAndFlags: ClinicAndFlags): void => {
    setCache('clinic', clinicAndFlags)
    setClinic(
      buildClinicFromInterface(clinicAndFlags?.clinic, clinicAndFlags?.flags),
    )
  }, [])

  const signOut = useCallback((deep = false) => {
    if (deep) {
      removeCache('users')
      setAllUsers(null)
    }
    // @ts-ignore
    httpClient.clearToken()
    removeToken()
    removeRefreshToken()
    removeCache('user')
    removeCache('screening')
    removeCache('clinic')
    removeCache('zendesk-key')
    removeCache('embed')
    removeCache('patientData')
    removeCache('inactiveUser')
    removeCache('sendingType')
    removeCache('dependent')
    removeCache(AUTHENTICATED_2FA_TIME)
    removeCache(REQUESTED_2FA_TIME)
    window?.Appcues?.reset()

    setUser(null)
    setClinic(null)
    setToken(null)
  }, [])

  const authContextValue = useMemo(() => {
    return {
      user,
      token,
      refreshToken,
      signIn,
      choiceUser,
      setUserClinic,
      signOut,
      users,
      clinic,
      updateUser,
      updateAllUsers,
    }
  }, [
    choiceUser,
    clinic,
    setUserClinic,
    signIn,
    signOut,
    token,
    refreshToken,
    updateUser,
    user,
    users,
    updateAllUsers,
  ])

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuthContext = (): IAuthContext => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within a AuthProvider')
  }
  return context
}
